import { INewsletterConfig } from '../interface/INewsletter';
import { addClass, getLang, makeEl } from '../common/Utils';
import { getAssetsDomain, loadCss, loadJs } from './util';

const DEFAULT_PREORDER_VERSION = '2.1.0';

/**
 * @category newsletter
 * @description
 * 뉴스레터 구독 모듈.
 *
 * 실제 UI와 구독 처리는 사전예약 운영툴이 배포하는 원격 uikit(`preorder/export`)이 담당하고,
 * 이 모듈은 그 번들을 로드해 `window.ncui.preorder` 에 위임하는 어댑터다.
 * 원격 번들은 preorder-admin-service 의 산출물이라 ncui 안으로 들어오지 않는다.
 *
 * @example
 * const newsletter = new ncui.newsletter({ service: 'tl', alias: 'thronenewsletter' });
 * document.querySelector('.btn-subscribe').addEventListener('click', () => newsletter.open());
 */
export default class Newsletter {
  private readonly service: string;
  private readonly alias: string;
  private readonly el: string;
  private readonly version: string;
  private readonly assetsDomain: string;
  private readonly _lang?: string;
  private readonly _country?: string;
  private readonly _path?: string;

  private isReady = false;
  private isOpenReserved = false;

  constructor(_config: INewsletterConfig) {
    this.service = _config.service || '';
    this.alias = _config.alias || 'newsletter';
    this.el = _config.el || 'newsletter-wrap';
    this.version = _config.version || DEFAULT_PREORDER_VERSION;
    this.assetsDomain = _config.assetsDomain || getAssetsDomain();
    this._lang = _config.lang;
    this._country = _config.country;
    this._path = _config.path;

    this.mount();
  }

  private async mount(): Promise<void> {
    this.createWrap();

    await this.loadUikit();
    await this.init();

    this.isReady = true;

    // uikit 로드 전에 open()이 호출된 경우 예약 처리한다.
    if (this.isOpenReserved) {
      this.isOpenReserved = false;
      this.open();
    }
  }

  /** 원격 uikit의 `modules[].element`와 동일한 id. 외부 HTML에 없을 수 있어 런타임에 만든다. */
  private createWrap(): void {
    if (document.getElementById(this.el)) return;

    makeEl('div', document.body, { id: this.el });
  }

  private async loadUikit(): Promise<void> {
    const base = `${this.assetsDomain}/uikit/preorder/export/${this.version}`;

    // preorder 모듈과 동일한 uikit을 공유하므로 중복 로드를 막는다.
    loadCss(`${base}/css/index.css`);

    if (window.ncui?.preorder) return;

    await Promise.all([
      loadJs(`${base}/js/index.js`),
      loadJs(`${base}/js/chunk-vendors.js`),
    ]);
  }

  /** init()은 Promise를 반환하므로 컴포넌트 마운트 완료까지 await 해야 한다. */
  private init(): Promise<void> {
    const el = document.getElementById(this.el);
    if (el) addClass(el, 'ncui-newsletter');

    return window.ncui.preorder.init(this.newsletterConfig);
  }

  /** 구독 모달을 연다. uikit 로드 전에 호출해도 로드 완료 시점에 자동 실행된다. */
  public open(): void {
    if (!this.isReady) {
      this.isOpenReserved = true;
      return;
    }

    if (typeof window.ncui?.preorder?.openNewsLetter !== 'function') {
      console.error('[ncui.newsletter] 뉴스레터 uikit이 로드되지 않았습니다.');
      return;
    }

    window.ncui.preorder.openNewsLetter();
  }

  /** 모듈을 언마운트 후 재마운트한다. */
  public reset(): void {
    if (typeof window.ncui?.preorder?.reset !== 'function') {
      console.error('[ncui.newsletter] 뉴스레터 uikit이 로드되지 않았습니다.');
      return;
    }

    window.ncui.preorder.reset();
  }

  private get modules() {
    return [{ name: 'newsLetter', element: this.el }];
  }

  private get newsletterConfig() {
    return {
      alias: this.alias,
      service: this.service,
      lang: this._lang ?? this.lang,
      country: this._country ?? this.country,
      path: this._path ?? this.path,
      modules: this.modules,
    };
  }

  // 원격 uikit·백엔드는 ko / kr 형태를 기대한다.
  private get lang(): string {
    return getLang() || '';
  }

  private get country(): string {
    return document.documentElement.getAttribute('data-country') || '';
  }

  private get path(): string {
    return `/${this.lang}-${this.country}`;
  }
}
